import React, { useState } from 'react';
import { Modal, Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import axiosInstance from '../../helpers/api';

/**
 * KOPS-518: "List My Home" conversion modal
 *
 * Appears on the CMA report page after seller sees their valuation.
 * Captures: name, phone, email, timeline, optional photos.
 * Enriches the anonymous lead and alerts Mike via SMS + email.
 */

interface ListMyHomeModalProps {
  show: boolean;
  onHide: () => void;
  leadId: string;
  address: string;
  estimatedValue?: number;
}

const ListMyHomeModal: React.FC<ListMyHomeModalProps> = ({ show, onHide, leadId, address, estimatedValue }) => {
  const [step, setStep] = useState<'form' | 'submitting' | 'success'>('form');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    timeline: '',
    notes: ''
  });
  const [photos, setPhotos] = useState<File[]>([]);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.phone) {
      setError('Name and phone number are required.');
      return;
    }

    setStep('submitting');
    setError('');

    try {
      // Enrich the anonymous lead with contact info
      await axiosInstance.patch(`/sellers/public-valuation/${leadId}/enrich`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        timeline: formData.timeline,
        notes: formData.notes
      });

      // Upload photos if any
      if (photos.length > 0) {
        const photoFormData = new FormData();
        photos.forEach(photo => {
          photoFormData.append('photos', photo);
        });
        photoFormData.append('leadId', leadId);

        try {
          await axiosInstance.post(`/sellers/public-valuation/${leadId}/photos`, photoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch {
          // Photo upload is non-blocking — lead is already saved
          console.warn('Photo upload failed, but lead was saved');
        }
      }

      setStep('success');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      setStep('form');
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setPhotos(Array.from(e.target.files).slice(0, 10) as File[]); // Max 10 photos
    }
  };

  const colors = {
    navy: '#0F1E35',
    gold: '#C9952A',
    gold2: '#E8B44A',
    muted: '#8FA3BF'
  };

  if (step === 'success') {
    return (
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Body style={{ background: colors.navy, color: 'white', borderRadius: '12px', padding: '48px', textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎉</div>
          <h2 style={{ marginBottom: '12px' }}>You're All Set!</h2>
          <p style={{ color: colors.gold2, fontSize: '18px', marginBottom: '8px' }}>
            Our broker Michael Boykin will contact you within 1 hour.
          </p>
          <p style={{ color: colors.muted, fontSize: '14px', marginBottom: '24px' }}>
            We'll review your property details and prepare a personalized listing strategy for {address}.
          </p>
          <div style={{ background: 'rgba(201,149,42,0.15)', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
            <p style={{ color: colors.muted, fontSize: '13px', margin: 0 }}>
              While you wait, feel free to continue chatting with AI about your home's value, market timing, or pricing strategy.
            </p>
          </div>
          <Button
            onClick={onHide}
            style={{ background: colors.gold, border: 'none', color: colors.navy, fontWeight: 700, padding: '12px 32px' }}
          >
            Continue Chatting with AI
          </Button>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Body style={{ background: colors.navy, color: 'white', borderRadius: '12px', padding: '32px' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h2 style={{ marginBottom: '8px' }}>List My Home</h2>
          <p style={{ color: colors.muted, fontSize: '14px', margin: 0 }}>
            {address}
            {estimatedValue && (
              <span style={{ color: colors.gold2, marginLeft: '8px' }}>
                · AI Value: ${estimatedValue.toLocaleString()}
              </span>
            )}
          </p>
        </div>

        {step === 'submitting' ? (
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <Spinner animation="border" variant="warning" style={{ width: '48px', height: '48px', marginBottom: '16px' }} />
            <p style={{ color: colors.gold2 }}>Sending to our broker...</p>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: colors.muted, fontSize: '13px' }}>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="Your first name"
                    required
                    style={{ background: '#162844', border: '1px solid rgba(201,149,42,0.3)', color: 'white' }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: colors.muted, fontSize: '13px' }}>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Your last name"
                    style={{ background: '#162844', border: '1px solid rgba(201,149,42,0.3)', color: 'white' }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: colors.muted, fontSize: '13px' }}>Phone *</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(602) 555-1234"
                    required
                    style={{ background: '#162844', border: '1px solid rgba(201,149,42,0.3)', color: 'white' }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: colors.muted, fontSize: '13px' }}>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@email.com"
                    style={{ background: '#162844', border: '1px solid rgba(201,149,42,0.3)', color: 'white' }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label style={{ color: colors.muted, fontSize: '13px' }}>When do you want to sell?</Form.Label>
              <div className="d-flex gap-2">
                {[
                  { value: 'asap', label: 'ASAP' },
                  { value: '1-3_months', label: '1-3 Months' },
                  { value: 'exploring', label: 'Just Exploring' }
                ].map(opt => (
                  <Button
                    key={opt.value}
                    variant={formData.timeline === opt.value ? 'warning' : 'outline-secondary'}
                    onClick={() => setFormData({ ...formData, timeline: opt.value })}
                    style={{
                      flex: 1,
                      ...(formData.timeline === opt.value
                        ? { background: colors.gold, color: colors.navy, border: 'none', fontWeight: 700 }
                        : { color: colors.muted, borderColor: 'rgba(201,149,42,0.3)' })
                    }}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ color: colors.muted, fontSize: '13px' }}>
                Photos of your home (optional — helps us confirm pricing)
              </Form.Label>
              <Form.Control
                type="file"
                multiple
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                style={{ background: '#162844', border: '1px solid rgba(201,149,42,0.3)', color: 'white' }}
              />
              {photos.length > 0 && (
                <small style={{ color: colors.gold2 }}>{photos.length} photo{photos.length > 1 ? 's' : ''} selected</small>
              )}
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label style={{ color: colors.muted, fontSize: '13px' }}>Anything else we should know?</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Recent upgrades, preferred timeline, questions..."
                style={{ background: '#162844', border: '1px solid rgba(201,149,42,0.3)', color: 'white' }}
              />
            </Form.Group>

            {error && (
              <div style={{ color: '#ff6b7a', fontSize: '14px', marginBottom: '16px', textAlign: 'center' }}>
                {error}
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              className="w-100"
              style={{ background: colors.gold, border: 'none', color: colors.navy, fontWeight: 700, padding: '14px', fontSize: '18px' }}
            >
              List My Home
            </Button>

            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <small style={{ color: colors.muted }}>
                Our broker reviews within 1 hour. No obligation. Cancel anytime.
              </small>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ListMyHomeModal;
